import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { SvgIconComponent } from "@mui/icons-material";
import { FONTS } from "../constants/styles";
import BottomButton from "./BottomButton";

interface StatusScreenProps {
    icon: SvgIconComponent;
    iconColor: string;
    title: string;
    subtitle: string;
    description?: string;
    buttonLabel: string;
    onButtonClick: () => void;
    buttonColor?: "primary" | "secondary" | "outlined" | "black";
    secondaryButtonLabel?: string;
    onSecondaryClick?: () => void;
}

const StatusScreen: React.FC<StatusScreenProps> = ({
    icon: Icon,
    iconColor,
    title,
    subtitle,
    description,
    buttonLabel,
    onButtonClick,
    buttonColor = "primary",
    secondaryButtonLabel,
    onSecondaryClick,
}) => {
    const hasSecondary = secondaryButtonLabel && onSecondaryClick;

    return (
        <>
            <Stack
                flex={1}
                alignItems="center"
                justifyContent="center"
                px={4}
                sx={{
                    "@keyframes statusFadeIn": {
                        from: { opacity: 0, transform: "translateY(16px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                }}
            >
                {/* Icon box */}
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "24px",
                        bgcolor: alpha(iconColor, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 auto",
                        color: iconColor,
                        mb: 3.5,
                        animation: "statusFadeIn 0.5s ease-out both",
                    }}
                >
                    <Icon sx={{ fontSize: 36 }} />
                </Box>

                {/* Title */}
                <Typography
                    fontFamily={FONTS.display}
                    fontWeight={700}
                    fontSize="24px"
                    lineHeight="30px"
                    textAlign="center"
                    color="text.primary"
                    sx={{
                        mb: 1.5,
                        animation: "statusFadeIn 0.5s ease-out 0.08s both",
                    }}
                >
                    {title}
                </Typography>

                {/* Subtitle */}
                <Typography
                    fontSize="16px"
                    lineHeight="24px"
                    textAlign="center"
                    color="text.secondary"
                    sx={{
                        maxWidth: 320,
                        animation: "statusFadeIn 0.5s ease-out 0.16s both",
                    }}
                >
                    {subtitle}
                </Typography>

                {/* Optional description */}
                {description && (
                    <Typography
                        fontSize="14px"
                        lineHeight="20px"
                        textAlign="center"
                        color="text.secondary"
                        sx={{
                            mt: 1,
                            maxWidth: 300,
                            opacity: 0.7,
                            animation: "statusFadeIn 0.5s ease-out 0.24s both",
                        }}
                    >
                        {description}
                    </Typography>
                )}
            </Stack>

            {/* Sticky buttons at bottom */}
            {hasSecondary && (
                <BottomButton
                    onClick={onSecondaryClick}
                    color="secondary"
                    sticky
                    sx={{ bottom: 96, mb: "0 !important" }}
                    noLoading
                >
                    {secondaryButtonLabel}
                </BottomButton>
            )}
            <BottomButton onClick={onButtonClick} color={buttonColor} sticky noLoading>
                {buttonLabel}
            </BottomButton>
        </>
    );
};

export default StatusScreen;
